if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = false;
      this.onViewportChange = this.init.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items, 10) || 4;
      this.tabletItems = parseInt(this.dataset.tabletItems, 10) || 2;
      this.mobileItems = parseInt(this.dataset.mobileItems, 10) || 1;
      this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 0;
      this.autoplay =
        this.dataset.autoplay === 'true' && this.autoplayDelay > 0;

      if (!this.enableSlider || !this.sliderWrapper) return;

      this.init();

      document.addEventListener('matchMobile', this.onViewportChange);
      document.addEventListener('unmatchMobile', this.onViewportChange);

      if (Shopify.designMode) {
        document.addEventListener(
          'shopify:block:select',
          this.onBlockSelect
        );
      }
    }

    init() {
      // Keep the carousel active on mobile when autoplay is enabled.
      // Otherwise, preserve the theme's original mobile grid/swipe behavior.
      if (FoxTheme.config.mqlMobile && !this.autoplay) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      if (this.sliderInstance) return;

      const gapValue = window
        .getComputedStyle(this.sliderWrapper)
        .getPropertyValue('--column-gap')
        .trim();

      const gapNumber = parseFloat(gapValue) || 0;
      const spaceBetween = gapValue.includes('px')
        ? gapNumber
        : gapNumber *
          (parseFloat(
            getComputedStyle(document.documentElement).fontSize
          ) || 10);

      const sliderOptions = {
        slidesPerView: this.mobileItems,
        spaceBetween,
        navigation: {
          nextEl: this.section?.querySelector(this.selectors.nextEl),
          prevEl: this.section?.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          768: {
            slidesPerView: this.tabletItems,
          },
          1280: {
            slidesPerView: this.items,
          },
        },
        loop: true,
        threshold: 2,
        ...(this.autoplay
          ? {
              autoplay: {
                delay: this.autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              },
            }
          : {}),
      };

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);

      this.sliderInstance = new window.FoxTheme.Carousel(
        this,
        sliderOptions,
        this.autoplay ? [FoxTheme.Swiper.Autoplay] : undefined
      );

      this.sliderInstance.init();

      const focusableElements =
        FoxTheme.a11y.getFocusableElements(this);

      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          if (!this.sliderInstance?.slider) return;

          const slide = element.closest('.swiper-slide');
          const index = Array.from(
            this.sliderInstance.slider.slides
          ).indexOf(slide);

          if (index >= 0) {
            this.sliderInstance.slider.slideTo(index);
          }
        });
      });
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);

      if (this.sliderInstance?.slider) {
        this.sliderInstance.slider.destroy(true, true);
        this.sliderInstance = false;
      }
    }

    handleBlockSelect(event) {
      if (
        event.detail.sectionId !== this.sectionId ||
        !this.sliderInstance?.slider
      ) {
        return;
      }

      const index = Number(event.target.dataset.index);
      if (Number.isNaN(index)) return;

      if (this.autoplay) {
        this.sliderInstance.slider.autoplay.stop();
      }

      this.sliderInstance.slider.slideToLoop(index);
    }

    disconnectedCallback() {
      document.removeEventListener(
        'matchMobile',
        this.onViewportChange
      );
      document.removeEventListener(
        'unmatchMobile',
        this.onViewportChange
      );
      document.removeEventListener(
        'shopify:block:select',
        this.onBlockSelect
      );

      if (this.sliderInstance?.slider) {
        this.sliderInstance.slider.destroy(true, true);
        this.sliderInstance = false;
      }
    }
  }

  customElements.define('collection-list', CollectionList);
}